<template>
    <div class="w-full max-w-225 self-center">
        <div v-if="error" class="mb-3 flex flex-col items-start gap-3">
            <BaseNotification theme="error" :text="error" :hide-dismiss="!!createdChatUrl" @dismiss="error = null" />
            <BaseButton v-if="createdChatUrl" theme="secondary" :disabled="isSending" @click="handleTextSend({ content: '' })">{{ t("common.tryAgain") }}</BaseButton>
        </div>
        <ChatMessageControls
            :compact="compact"
            :auto-focus-input="autoFocusInput"
            :is-disabled="isTranscribing || isSending || !!createdChatUrl"
            :is-transcribing="isTranscribing"
            :document-upload-enabled="documentUploadEnabled"
            :visual-document-analysis-enabled="visualDocumentAnalysisEnabled"
            :work-document-capabilities-url="workDocumentCapabilitiesUrl"
            :default-text-input="defaultTextInput"
            :is-sending="isSending"
            :placeholder="placeholder"
            :location="CHAT_MESSAGE_CONTROLS_LOCATION.HOMEPAGE"
            :pii-patterns="piiPatterns"
            :better-voice-composer-enabled="betterVoiceComposerEnabled"
            @text-send="handleTextSend"
            @text-keydown="error = null"
            @recording-complete="handleRecordingComplete"
            @upload-error="handleUploadError" />
    </div>
</template>

<script setup lang="ts">
import { progress, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { ChatInputFile, GlobalProperties } from "~vue/appTypes";
import ChatMessageControls from "~vue/ChatDetail/ChatMessageControls.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { showFullPageLoading } from "~vue/components/FullPageLoading.vue";
import { useChatInputPreference } from "~vue/composables/useChatInputPreference.js";
import { type AudioStats, useTranscription } from "~vue/composables/useTranscription";
import { useChatStore } from "~vue/stores/chatStore";
import { isInIFrame } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHAT_MESSAGE_CONTROLS_LOCATION, CHATSOURCE } from "~vue/utils/constants.js";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import type { PatternEntry } from "~vue/utils/pii/keywordClassifier";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getWafErrorMessage, WAF_ERROR_CAUSE } from "~vue/utils/wafDetection";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        compact?: boolean;
        autoFocusInput?: boolean;
        startChatUrl?: string;
        chatSource?: string;
        documentUploadEnabled: boolean;
        visualDocumentAnalysisEnabled?: boolean;
        workDocumentCapabilitiesUrl?: string;
        defaultChatRedirectUrl: string;
        placeholder?: string;
        piiPatterns?: PatternEntry[] | null;
        betterVoiceComposerEnabled?: boolean;
    }>(),
    {
        compact: false,
        autoFocusInput: true,
        startChatUrl: "/coach/start/",
        chatSource: CHATSOURCE.HOME_CHAT_MESSAGE,
        placeholder: "What's on your mind?",
        visualDocumentAnalysisEnabled: false,
        workDocumentCapabilitiesUrl: "",
        betterVoiceComposerEnabled: false,
    },
);

const userChatInputPreference = useChatInputPreference();
const defaultTextInput = computed(() => {
    return userChatInputPreference.value !== null ? userChatInputPreference.value : true;
});

const { $feedbackMailto } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);

const isSending = ref(false);
const createdChatUrl = ref<string | null>(null);
const error = ref<string | null>(null);
const { isTranscribing, transcribe } = useTranscription({ source: "ChatHome", error });

// Setup API for POST request to start chat
const startChatPayload = ref<{
    chat_source: string;
    initial_message: string;
    initial_message_files: ChatInputFile[];
} | null>(null);
const {
    data: startChatResponse,
    execute: executeStartChat,
    error: startChatError,
    isFetching: isStartingChat,
} = useFetch(() => props.startChatUrl, { immediate: false })
    .post(startChatPayload)
    .json();

const handleTextSend = async (textSendArgs: { content: string; source?: string; files?: ChatInputFile[] }) => {
    const { content, source = "text", files = useChatStore.files } = textSendArgs;
    const hasFiles = files.length > 0;
    // `isSending` is being used as a lock here to prevent creating multiple coaching sessions from
    // double-clicks on the submit button.
    if ((!createdChatUrl.value && !content?.trim() && !hasFiles) || isSending.value || isStartingChat.value) {
        return;
    }
    isSending.value = true;
    error.value = null;

    try {
        progress.start();

        let redirectUrl = createdChatUrl.value;
        if (!redirectUrl) {
            const uce_id = crypto.randomUUID();
            if (props.chatSource === CHATSOURCE.HOME_CHAT_MESSAGE) {
                logUserInteraction(AmplitudeEventNames.HOMEPAGE_CHAT_STARTED, { source });
            }
            logUserInteraction(AmplitudeEventNames.USER_MESSAGE_SENT, {
                uce_id,
                mic: source === "voice",
                chat_source: props.chatSource,
            });

            // Prepare POST payload (sensitive data in body, not URL).
            const initialMessageFiles = files.filter((f) => "id" in f || "content" in f);
            startChatPayload.value = {
                chat_source: props.chatSource,
                initial_message: content,
                initial_message_files: initialMessageFiles,
            };

            await executeStartChat();

            if (startChatError.value) {
                throw startChatError.value;
            }
            redirectUrl = startChatResponse.value?.redirect_url;
            if (!redirectUrl) {
                throw new Error("No redirect URL received");
            }
            // The session and attachments already exist even if the following navigation fails.
            createdChatUrl.value = redirectUrl;
        }

        if (isInIFrame()) {
            useChatStore.removeAllFiles();
            showFullPageLoading();
            globalThis.location.href = redirectUrl;
        } else {
            const navigated = await new Promise<boolean>((resolve) => {
                let succeeded = false;
                router.visit(redirectUrl, {
                    onSuccess: () => {
                        succeeded = true;
                    },
                    onFinish: () => resolve(succeeded),
                });
            });
            if (!navigated) {
                throw new Error(t("chat.composer.openError"));
            }
            useChatStore.removeAllFiles();
        }
    } catch (e) {
        // The session-ended dialog is already up and is modal, so a banner behind it would only
        // contradict it. Not logged either -- an expired session is not a defect (ENG-1667).
        if (isSessionEndedError(e)) {
            isSending.value = false;
            return;
        }
        if (e instanceof Error && e.cause === WAF_ERROR_CAUSE) {
            error.value = getWafErrorMessage($feedbackMailto);
        } else {
            error.value = e instanceof Error && e.message ? e.message : "Something went wrong. Please try again.";
        }
        logError(e);
        isSending.value = false;
    } finally {
        // Always finish the POST request progress indicator
        progress.finish();
    }
};

const handleRecordingComplete = async ({ blob, audioStats }: { blob: Blob; audioStats?: AudioStats & { initToStartMs?: number } }) => {
    const transcript = await transcribe({ blob, audioStats });
    if (transcript) {
        handleTextSend({ content: transcript, source: "voice" });
    }
};

const handleUploadError = (message: string | null) => {
    error.value = message;
};
</script>
